import { useEffect, useState } from "react";
import { Quote } from "lucide-react";
import api from "../lib/api";
import { useFlash } from "../lib/flash";
import LogoUpload from "./LogoUpload";

const CONSENT = "Jeg giver JaTak Pro tilladelse til at vise citatet, mit navn og butikkens navn på jatakpro.dk. Jeg kan til enhver tid trække tilladelsen tilbage.";
const STATUS = { pending: ["pending", "Afventer godkendelse"], approved: ["picked_up", "Vises på jatakpro.dk"], hidden: ["cancelled", "Vises ikke"] };

export function TField({ k, label, v, setV, errors, max, area, testid }) {
  const Tag = area ? "textarea" : "input";
  return (
    <div className="field">
      <label htmlFor={`t-${testid}-${k}`}>{label}</label>
      <Tag id={`t-${testid}-${k}`} value={v[k] || ""} maxLength={max} rows={area ? 3 : undefined}
        onChange={(e) => setV({ ...v, [k]: e.target.value })} data-testid={`${testid}-${k}`} />
      {area && <div className="hint">{(v[k] || "").length}/{max} tegn</div>}
      {errors[k] && <div className="err" data-testid={`${testid}-${k}-error`}>{errors[k]}</div>}
    </div>
  );
}

export default function TestimonialCard() {
  const flash = useFlash();
  const [d, setD] = useState(null);
  const [v, setV] = useState({});
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/testimonials/mine").then((r) => { setD(r.data); setV((x) => ({ shop_name: r.data.shop_name, ...x })); });
  useEffect(() => { load(); }, []);
  if (!d) return null;
  const active = d.items.find((t) => t.status !== "hidden" || t.consent?.given);
  const last = d.items[0];

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErrors({});
    try {
      const r = await api.post("/testimonials", v);
      flash(r.data.flash);
      setV({ shop_name: d.shop_name });
      await load();
    } catch (ex) {
      if (ex.response?.data?.errors) setErrors(ex.response.data.errors);
      else flash(ex.response?.data?.detail || "Kunne ikke sende", "error");
    } finally { setBusy(false); }
  };
  const withdraw = async () => {
    if (!window.confirm("Træk tilladelsen tilbage? Udtalelsen fjernes straks fra jatakpro.dk.")) return;
    const r = await api.post(`/testimonials/${active.id}/withdraw`);
    flash(r.data.flash);
    load();
  };

  return (
    <section className="panel tc" data-testid="testimonial-card">
      <h2><Quote size={18} /> Del din oplevelse med JaTak Pro</h2>
      {active ? (
        <div className="tc-mine" data-testid="testimonial-mine">
          <span className={`pill ${STATUS[active.status][0]}`} data-testid="testimonial-mine-status">{STATUS[active.status][1]}</span>
          <blockquote>"{active.quote}"</blockquote>
          <p className="muted">– {active.person_name}{active.role ? `, ${active.role}` : ""}, {active.shop_name}{active.city ? `, ${active.city}` : ""}</p>
          <button type="button" className="btn small" onClick={withdraw} data-testid="testimonial-withdraw">Træk tilladelse tilbage</button>
        </div>
      ) : (
        <form onSubmit={submit} noValidate data-testid="testimonial-form">
          <p className="muted">Har JaTak Pro gjort hverdagen lettere? Skriv et par ord – vi viser det på jatakpro.dk, når vi har kigget på det.</p>
          {last && last.consent?.withdrawn_at && <p className="muted" data-testid="testimonial-withdrawn-note">Din tidligere udtalelse er trukket tilbage og vises ikke.</p>}
          <TField k="quote" label="Dit citat" v={v} setV={setV} errors={errors} max={280} area testid="testimonial" />
          <div className="tc-grid">
            <TField k="person_name" label="Navn" v={v} setV={setV} errors={errors} max={80} testid="testimonial" />
            <TField k="role" label="Rolle (fx Uddeler)" v={v} setV={setV} errors={errors} max={60} testid="testimonial" />
            <TField k="shop_name" label="Butikkens navn" v={v} setV={setV} errors={errors} max={100} testid="testimonial" />
            <TField k="city" label="By" v={v} setV={setV} errors={errors} max={60} testid="testimonial" />
          </div>
          <LogoUpload url={v.logo_url} onChange={(id, url) => setV({ ...v, logo_id: id, logo_url: url })} testid="testimonial-logo" />
          <label className="tc-consent">
            <input type="checkbox" checked={!!v.consent} onChange={(e) => setV({ ...v, consent: e.target.checked })} data-testid="testimonial-consent" />
            <span>{CONSENT}</span>
          </label>
          {errors.consent && <div className="err" data-testid="testimonial-consent-error">{errors.consent}</div>}
          <button className="btn primary" disabled={busy} data-testid="testimonial-submit">{busy ? "Sender…" : "Send udtalelse"}</button>
        </form>
      )}
    </section>
  );
}
